import React, { useState, useEffect, useCallback, useRef, useMemo } from "react";
import axios from "axios";
import { API, useApp } from "../context";
import { Star, Zap, Trophy } from "lucide-react";

/* Clickable collectible that floats across the sky */
const ANIMATION_STYLES = {
  "shooting-star": "collectible-streak",
  "pearl": "collectible-bubble",
  "scarab": "collectible-hover",
  "firefly": "collectible-flutter",
  "snowflake": "collectible-drift",
  "candy": "collectible-bounce",
  "sparkler": "collectible-burst-float",
  "lightning": "collectible-zap",
  "ankh": "collectible-hover",
  "ember": "collectible-flicker",
  "shell": "collectible-drift",
};

const Collectible = ({ item, onCatch, index }) => {
  const [caught, setCaught] = useState(false);
  const pos = useMemo(() => ({
    startX: 5 + Math.random() * 85,
    startY: 5 + Math.random() * 55,
    dur: 6 + Math.random() * 8,
    delay: index * 1.5 + Math.random() * 3,
  }), [index]);

  const handleClick = (e) => {
    e.stopPropagation();
    if (caught) return;
    setCaught(true);
    onCatch(item);
  };

  if (caught) return (
    <div className="absolute pointer-events-none z-[60]" style={{
      left: `${pos.startX}%`, top: `${pos.startY}%`,
      animation: "catch-burst 0.6s ease-out forwards",
    }}>
      <span className="text-yellow-300 font-bold text-sm drop-shadow-lg">+{item.points}</span>
    </div>
  );

  const animClass = ANIMATION_STYLES[item.type] || "collectible-float";

  return (
    <div className={`sky-collectible ${animClass}`} onClick={handleClick}
      data-testid={`sky-collectible-${index}`}
      style={{
        left: `${pos.startX}%`, top: `${pos.startY}%`,
        animationDuration: `${pos.dur}s`,
        animationDelay: `${pos.delay}s`,
      }}>
      <div className="sky-collectible-inner" style={{
        boxShadow: `0 0 15px ${item.points >= 4 ? '#FBBF24' : '#A78BFA'}80`,
      }}>
        <span className="text-lg">{item.icon}</span>
      </div>
    </div>
  );
};

/* Coalesced catch-feedback toast, when the player catches multiple
 * collectibles in a row, a SINGLE toast shows the running total and
 * extends its dismissal timer by another 3s with each catch instead
 * of stacking N toasts. After 3s of no new catches, it fades out.
 *
 *   props:
 *     • message      latest catch's text (e.g. "+1 Star")
 *     • count        running tally (3 = "you caught 3 in a row")
 *     • totalStars   sum of stars rewarded across the streak
 *     • lastTs       timestamp of the last catch, used as the effect
 *                    dependency so the dismissal timer resets each catch
 *     • onDone       callback fired when the streak quiets down 3s
 */
const CatchToast = ({ message, count, totalStars, lastTs, onDone }) => {
  useEffect(() => {
    const t = setTimeout(onDone, 3000);
    return () => clearTimeout(t);
  }, [lastTs, onDone]);
  return (
    <div className="below-header-fixed left-1/2 -translate-x-1/2 z-[100] px-4 py-2 rounded-full bg-yellow-500/95 border border-yellow-400 text-yellow-950 text-sm font-bold shadow-lg shadow-yellow-500/30 animate-bounce" data-testid="catch-toast">
      {count > 1 ? (
        <span>
          +{totalStars} Stars · {count} caught in a row 🌟
        </span>
      ) : (
        message
      )}
    </div>
  );
};

/* Theme Event Progress Bar */
const ThemeEventBar = ({ event, progress, onClaim }) => {
  const [visible, setVisible] = useState(true);
  const [fading, setFading] = useState(false);

  useEffect(() => {
    if (!event || !progress) return;
    const fadeTimer = setTimeout(() => setFading(true), 4000);
    const hideTimer = setTimeout(() => setVisible(false), 5000);
    return () => { clearTimeout(fadeTimer); clearTimeout(hideTimer); };
  }, [event, progress]);

  if (!event || !progress || !visible) return null;

  return (
    <div className={`below-header-fixed left-1/2 -translate-x-1/2 z-[45] w-full max-w-xs px-4 transition-opacity duration-1000 ${fading ? 'opacity-0' : 'opacity-100'}`} data-testid="theme-event-bar">
      <div className="rounded-xl bg-black/30 border border-white/10 px-3 py-2 shadow-lg">
        <div className="flex items-center gap-2">
          <span className="text-sm">{event.icon}</span>
          <p className="text-white text-[10px] font-bold flex-1 truncate">{event.name}</p>
          <span className="text-white/50 text-[10px] font-mono">{progress.collected}/{progress.goal}</span>
        </div>
        <div className="w-full h-1.5 bg-white/10 rounded-full overflow-hidden mt-1.5">
          <div className="h-full rounded-full transition-all duration-500" style={{
            width: `${progress.progress_pct}%`,
            background: `linear-gradient(90deg, ${event.color}, ${event.color}CC)`,
            boxShadow: `0 0 8px ${event.color}60`,
          }} />
        </div>
        {progress.completed && !progress.reward_claimed && (
          <button onClick={onClaim} className="mt-1.5 w-full py-1 rounded-lg text-[10px] font-bold text-white transition-all hover:scale-[1.02]" style={{
            background: `linear-gradient(135deg, ${event.color}, ${event.color}CC)`,
          }} data-testid="claim-reward-btn">
            <Trophy size={10} className="inline mr-1" /> Claim {event.reward_stars} Stars!
          </button>
        )}
      </div>
    </div>
  );
};

/* Sky Game Stats Tooltip */
const SkyGameStats = ({ stats }) => {
  if (!stats) return null;
  return (
    <div className="below-header-fixed right-4 z-[40] pointer-events-auto" data-testid="sky-stats">
      <div className="rounded-xl bg-black/40 backdrop-blur-sm border border-white/15 px-3 py-1.5 flex items-center gap-2 shadow-lg">
        <Zap size={12} className="text-yellow-400" />
        <span className="text-yellow-300 text-xs font-bold">{stats.today_catches}</span>
        <span className="text-white/30 text-[9px]">/{stats.daily_limit}</span>
      </div>
    </div>
  );
};

/* ====== MAIN SKY GAME CONTROLLER ====== */
const InteractiveSkyGame = () => {
  const { user } = useApp();
  const [collectible, setCollectible] = useState(null);
  const [event, setEvent] = useState(null);
  const [progress, setProgress] = useState(null);
  const [stats, setStats] = useState(null);
  const [toasts, setToasts] = useState([]);
  const [streak, setStreak] = useState(null); // { message, count, totalStars, lastTs }
  const [items, setItems] = useState([]);
  const spawnRef = useRef(null);
  const toastIdRef = useRef(0);
  const uid = user?.id || user?.email;

  // Load data
  useEffect(() => {
    axios.get(`${API}/sky-game/collectibles`).then(r => setCollectible(r.data.collectible)).catch(() => {});
    axios.get(`${API}/theme-events/current`).then(r => setEvent(r.data.event)).catch(() => {});
    if (uid) {
      axios.get(`${API}/theme-events/progress/${uid}`).then(r => setProgress(r.data)).catch(() => {});
      axios.get(`${API}/sky-game/stats/${uid}`).then(r => setStats(r.data)).catch(() => {});
    }
  }, [uid]);

  // Spawn collectibles periodically
  useEffect(() => {
    if (!collectible) return;
    const rate = collectible.spawn_rate || 0.4;
    const interval = (1 / rate) * 3000;
    let id = 0;
    const spawn = () => {
      id++;
      setItems(prev => {
        if (prev.length >= 6) return prev;
        return [...prev, { ...collectible, key: Date.now() + id }];
      });
    };
    spawn(); // initial
    spawnRef.current = setInterval(spawn, interval);
    return () => clearInterval(spawnRef.current);
  }, [collectible]);

  // Remove old items
  useEffect(() => {
    const cleanup = setInterval(() => {
      setItems(prev => prev.slice(-6));
    }, 15000);
    return () => clearInterval(cleanup);
  }, []);

  const handleCatch = useCallback(async (item) => {
    if (!uid) return;
    setItems(prev => prev.filter(i => i.key !== item.key));
    try {
      const r = await axios.post(`${API}/sky-game/collect`, null, {
        params: { user_id: uid, item_type: item.type }
      });
      if (r.data.success) {
        // Coalesce multiple catches into ONE toast that updates count
        // + extends its lifetime, instead of stacking N notifications.
        const starsThisCatch = (typeof r.data.stars_awarded === "number") ? r.data.stars_awarded : 1;
        setStreak(prev => {
          if (!prev) {
            return { message: r.data.message, count: 1, totalStars: starsThisCatch, lastTs: Date.now() };
          }
          return {
            message: r.data.message,
            count: prev.count + 1,
            totalStars: prev.totalStars + starsThisCatch,
            lastTs: Date.now(),
          };
        });
        setStats(prev => prev ? { ...prev, today_catches: prev.today_catches + 1, remaining_today: prev.remaining_today - 1 } : prev);
        if (progress) {
          setProgress(prev => {
            const newCollected = Math.min(prev.collected + 1, prev.goal);
            return { ...prev, collected: newCollected, progress_pct: Math.min(100, Math.floor((newCollected / prev.goal) * 100)), completed: newCollected >= prev.goal };
          });
        }
      }
    } catch (e) {}
  }, [uid, progress]);

  const handleClaimReward = async () => {
    if (!uid || !event) return;
    try {
      const r = await axios.post(`${API}/theme-events/claim-reward`, null, {
        params: { user_id: uid, event_id: event.id }
      });
      if (r.data.success) {
        setProgress(prev => ({ ...prev, reward_claimed: true }));
        const tid = ++toastIdRef.current;
        setToasts(prev => [...prev, { id: tid, message: r.data.message }]);
      }
    } catch (e) {}
  };

  return (
    <>
      {/* Floating collectibles */}
      <div className="absolute inset-0 pointer-events-none z-[15]">
        {items.map((item, i) => (
          <Collectible key={item.key} item={item} index={i} onCatch={handleCatch} />
        ))}
      </div>

      {/* Stats display */}
      {uid && stats && <SkyGameStats stats={stats} />}

      {/* Theme event progress */}
      {uid && <ThemeEventBar event={event} progress={progress} onClaim={handleClaimReward} />}

      {/* Catch toasts, one coalesced streak toast + any reward-claim toasts */}
      {streak && (
        <CatchToast
          key="streak"
          message={streak.message}
          count={streak.count}
          totalStars={streak.totalStars}
          lastTs={streak.lastTs}
          onDone={() => setStreak(null)}
        />
      )}
      {toasts.map(t => (
        <CatchToast key={t.id} message={t.message} count={1} totalStars={0} lastTs={t.id} onDone={() => setToasts(prev => prev.filter(x => x.id !== t.id))} />
      ))}
    </>
  );
};

export default InteractiveSkyGame;
